<template>
  <section class="flex flex-col gap-12 items-center justify-center overflow-hidden px-4 py-16 relative w-full bg-white md:px-16">
    <div class="mx-auto w-full max-w-[1200px] min-w-0">
      <div class="flex w-full flex-col items-center gap-12">
        <div class="flex w-full flex-col gap-1 text-center">
          <p
            v-if="overlineText"
            class="text-sm font-semibold uppercase leading-5 tracking-[1px]"
          >
            {{ overlineText }}
          </p>
          <h2 class="text-[32px] font-medium leading-10 tracking-[-0.02em] lg:text-[36px] lg:leading-[44px]">
            {{ headingText }}
          </h2>
        </div>

        <div class="flex w-full flex-col items-center gap-1 lg:gap-4">
          <SpecialisationFilterList
            v-model="selectedSpecialisationId"
            :specialisations="specialisations"
            :loading="loading"
            variant="page"
            :show-heading="false"
            section-class="w-full min-w-0"
            show-all-option
            scrollable-mobile
            compact
          />

          <p v-if="loading" class="w-full text-center text-base text-neutral-500">
            {{ t('specialists.loading') }}
          </p>

          <p
            v-else-if="filteredSpecialists.length === 0"
            class="w-full text-center text-base text-neutral-500"
          >
            {{ t('specialists.empty_carousel') }}
          </p>

          <!--
            -mx-8 + w-[calc(100%+4rem)]: viewport rozszerzony 32px na każdą stronę → cienie kart (zasięg ~32px) mają miejsce.
            Wrapper px-8 wewnątrz kompensuje rozszerzenie → --card-width opiera się na 1200px.
          -->
          <div
            v-else
            ref="viewportRef"
            class="specialists-carousel-viewport -mx-8 w-[calc(100%+4rem)] cursor-grab overflow-hidden touch-pan-y [&_img]:[-webkit-user-drag:none]"
            :class="viewportClass"
            v-bind="swipeHandlers"
          >
            <div class="px-8 py-1 lg:py-3">
              <div
                data-carousel-track
                class="flex gap-3 lg:gap-5"
                :class="[trackClass, trackTransitionClass]"
                :style="trackStyle"
              >
              <div
                v-for="specialist in filteredSpecialists"
                :key="specialist.id"
                class="w-(--card-width) shrink-0 min-w-0"
              >
                <SpecialistCard :specialist="specialist" clickable-card />
              </div>
              </div>
            </div>
          </div>

          <template v-if="!loading && filteredSpecialists.length > pageSize">
          <!-- Mobile -->
          <div class="flex lg:hidden h-11 w-full items-center justify-between">
            <CarouselDots
              :count="dotCount"
              :active-index="slideIndex"
              :dot-size="8"
              :gap="8"
            >
              <template #default="{ index, active }">
                <button
                  type="button"
                  class="size-2 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-all duration-300 ease-in-out"
                  :class="active ? 'opacity-100 scale-125' : 'opacity-30 scale-100'"
                  :style="{ backgroundColor: '#262626' }"
                  :aria-label="t('carousel.goto_slide', { n: index + 1 })"
                  @click="goToSlide(index)"
                />
              </template>
            </CarouselDots>

            <div class="flex items-center gap-2">
              <button
                type="button"
                class="flex size-11 shrink-0 items-center justify-center rounded-full border bg-white p-0 transition-opacity cursor-pointer disabled:cursor-default"
                :class="[accentBorderClass, canGoPrev ? 'hover:opacity-80' : 'opacity-40']"
                :aria-label="t('carousel.specialists_prev')"
                :disabled="!canGoPrev"
                @click="prev"
              >
                <svg
                  class="size-5"
                  :class="canGoPrev ? accentTextClass : 'text-neutral-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                type="button"
                class="flex size-11 shrink-0 items-center justify-center rounded-full border bg-white p-0 transition-opacity cursor-pointer disabled:cursor-default"
                :class="[accentBorderClass, canGoNext ? 'hover:opacity-80' : 'opacity-40']"
                :aria-label="t('carousel.specialists_next')"
                :disabled="!canGoNext"
                @click="next"
              >
                <svg class="size-5" :class="accentTextClass" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Desktop -->
          <div class="relative hidden lg:block h-11 w-full">
            <CarouselDots
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              :count="dotCount"
              :active-index="slideIndex"
              :dot-size="6"
              :gap="8"
            >
              <template #default="{ active }">
                <span
                  class="size-1.5 shrink-0 rounded-full transition-all duration-300 ease-in-out"
                  :class="active ? 'opacity-100 scale-125' : 'opacity-30 scale-100'"
                  :style="{ backgroundColor: '#262626' }"
                />
              </template>
            </CarouselDots>

            <button
              type="button"
              class="absolute right-0 top-1/2 size-11 -translate-y-1/2 cursor-pointer disabled:cursor-default"
              :aria-label="t('carousel.specialists_next')"
              :disabled="!canGoNext"
              @click="next"
            >
              <div
                class="absolute left-0 top-0 flex size-11 items-center justify-center rounded-full border bg-white transition-opacity"
                :class="[
                  accentBorderClass,
                  canGoNext ? 'hover:opacity-80' : 'opacity-40',
                ]"
              >
                <svg class="size-5" :class="accentTextClass" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            <button
              type="button"
              class="absolute right-14 top-1/2 size-11 -translate-y-1/2 cursor-pointer disabled:cursor-default"
              :aria-label="t('carousel.specialists_prev')"
              :disabled="!canGoPrev"
              @click="prev"
            >
              <div
                class="absolute left-0 top-0 flex size-11 items-center justify-center rounded-full border bg-white transition-colors"
                :class="[
                  accentBorderClass,
                  canGoPrev ? accentHoverBgClass : 'opacity-40',
                ]"
              >
                <svg
                  class="size-5"
                  :class="canGoPrev ? accentTextClass : 'text-neutral-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </div>
            </button>
          </div>
          </template>
        </div>
      </div>

      <div v-if="buttonLabel && !loading && filteredSpecialists.length" class="mt-12 flex justify-center">
        <Link
          :href="allSpecialistsHref"
          class="inline-flex h-12 items-center justify-center gap-2 rounded-lg border px-5 py-[14px] text-base font-semibold no-underline transition-colors"
          :class="[accentBorderClass, accentTextClass, accentHoverBgClass]"
        >
          {{ buttonLabel }}
          <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Link } from '@inertiajs/vue3';
import SpecialistCard from '@/Shared/SpecialistCard.vue';
import SpecialisationFilterList from '@/Shared/SpecialisationFilterList.vue';
import CarouselDots from '@/Shared/CarouselDots.vue';
import { specialists_index as specialistsIndexRoute } from '@/routes.js';
import { useTranslations } from '@/utils/translations.js';
import { useCarouselSwipe } from '@/utils/useCarouselSwipe.js';
import { carouselTrackTransform } from '@/utils/carouselTrack.js';

const { t } = useTranslations();

const props = defineProps({
  overlineText: { type: String, default: '' },
  headingText:  { type: String, default: 'Specjaliści' },
  buttonLabel:  { type: String, default: 'Zobacz wszystkich specjalistów' },
  buttonUrl:    { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
  // eslint-disable-next-line vue/prop-name-casing
  preview_mode: { type: Boolean, default: false },
});

const MOBILE_BREAKPOINT = 1024;

const ACCENT_BY_SITE = {
  code_medical_center: {
    border: 'border-code-medical-center',
    text: 'text-code-medical-center',
    hover: 'hover:bg-code-medical-center-secondary',
  },
  infertility_treatment_clinic: {
    border: 'border-infertility-clinic',
    text: 'text-infertility-clinic',
    hover: 'hover:bg-infertility-clinic-secondary',
  },
  family_clinic: {
    border: 'border-family-clinic',
    text: 'text-family-clinic',
    hover: 'hover:bg-family-clinic-secondary',
  },
  institute: {
    border: 'border-institute',
    text: 'text-institute',
    hover: 'hover:bg-institute-secondary',
  },
};

const accent = computed(() => (
  ACCENT_BY_SITE[props.site_context] || ACCENT_BY_SITE.family_clinic
));
// Figma carousel controls are neutral #262626 in every context; only the hover
// fill keeps the per-clinic tint.
const accentBorderClass = 'border-[#262626]';
const accentTextClass = 'text-[#262626]';
const accentHoverBgClass = computed(() => accent.value.hover);

const specialists = ref([]);
const loading = ref(true);
const selectedSpecialisationId = ref(null);
const slideIndex = ref(0);

async function loadSpecialists() {
  loading.value = true;
  try {
    const query = new URLSearchParams();
    if (props.site_context) query.set('site_context', props.site_context);
    const response = await fetch(`/api/specialists?${query.toString()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    specialists.value = Array.isArray(payload.specialists) ? payload.specialists : [];
  } catch {
    specialists.value = [];
  } finally {
    loading.value = false;
  }
}

// Filter badges derived from loaded specialists (same pattern as ServicesCarousel).
const specialisations = computed(() => {
  const map = new Map();
  specialists.value.forEach((specialist) => {
    (specialist.specialisations || []).forEach((spec) => {
      if (!map.has(spec.id)) map.set(spec.id, spec);
    });
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
});

const filteredSpecialists = computed(() => {
  if (selectedSpecialisationId.value == null) return specialists.value;
  return specialists.value.filter((specialist) => (
    (specialist.specialisations || []).some((spec) => spec.id === selectedSpecialisationId.value)
  ));
});

onMounted(() => {
  if (typeof window !== 'undefined') {
    mobileMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    syncMobileViewport = () => {
      isMobileViewport.value = mobileMediaQuery.matches;
    };
    syncMobileViewport();
    mobileMediaQuery.addEventListener('change', syncMobileViewport);
  }

  loadSpecialists();
});

onBeforeUnmount(() => {
  if (mobileMediaQuery && syncMobileViewport) {
    mobileMediaQuery.removeEventListener('change', syncMobileViewport);
  }
});

watch(selectedSpecialisationId, () => {
  slideIndex.value = 0;
});

const isMobileViewport = ref(false);
let mobileMediaQuery = null;
let syncMobileViewport = null;

const pageSize = computed(() => (isMobileViewport.value ? 1 : 3));

const maxSlideIndex = computed(() => Math.max(0, filteredSpecialists.value.length - pageSize.value));
const dotCount = computed(() => maxSlideIndex.value + 1);

const canGoPrev = computed(() => slideIndex.value > 0);
const canGoNext = computed(() => slideIndex.value < maxSlideIndex.value);

const {
  viewportRef,
  viewportClass,
  trackClass,
  trackTransitionClass,
  swipeHandlers,
} = useCarouselSwipe({
  onNext: () => next(),
  onPrev: () => prev(),
  enabled: () => !loading.value && filteredSpecialists.value.length > pageSize.value,
  canGoNext,
  canGoPrev,
});

const trackStyle = computed(() => carouselTrackTransform(
  `-1 * ${slideIndex.value} * (var(--card-width) + var(--card-gap))`,
));

watch(maxSlideIndex, (max) => {
  if (slideIndex.value > max) slideIndex.value = max;
});

function next() {
  if (!canGoNext.value) return;
  slideIndex.value += 1;
}

function prev() {
  if (!canGoPrev.value) return;
  slideIndex.value -= 1;
}

function goToSlide(index) {
  slideIndex.value = Math.min(Math.max(0, index), maxSlideIndex.value);
}

const allSpecialistsHref = computed(() => {
  if (props.buttonUrl && props.buttonUrl !== '#') return props.buttonUrl;
  return specialistsIndexRoute();
});
</script>

<style scoped>
.specialists-carousel-viewport {
  --card-width: 100%;
  --card-gap: 0.75rem;
}

@media (min-width: 1024px) {
  .specialists-carousel-viewport {
    --card-gap: 1.25rem;
    --card-width: calc((100% - 2 * var(--card-gap)) / 3);
  }
}
</style>
